import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { commonClient, ordersClient } from 'api';
import type {
  Attachment,
  CancelOrderRequest,
  OrderCustomFormToAdd,
  OrderCustomFormToEdit,
  TableResultMaterialForCustomForm
} from 'api/models/mainservice';
import type { ApiError, CustomFormMaterialTableQuery, TableResult } from 'common/models';
import { handleApiErrorWithNotification } from 'helpers/errorHandling';
import { getInfiniteNextPageParam } from 'helpers/queriesHelper';
import { defaultMaterialTableQuery, getTableQueryKey } from 'helpers/tableHelpers';

import type {
  ExistingOrder,
  ExistingOrderQuery,
  OrderAlt,
  OrderDetail,
  OrderDetailAlt,
  OrderTableQuery,
  OrderToEdit,
  PublicGetPublicOrderDataQueryResult
} from './models';

const ordersKey = 'orders';
const orderDetailKey = 'orderDetail';
const customFormMaterialKey = 'customFormMaterial';

export const getOrdersQueryKeyBuilder = () => [ordersKey];
export const getOrderDetailQueryKeyBuilder = (id: string) => [orderDetailKey, id];
export const getOrderDetailQueryKeyBuilderV2 = (id: string) => [orderDetailKey, id, 'v2'];

export const getOrderDetailQueryKeyBuilderSimple = (
  orderId: string,
  interventionId?: string | null,
  customFormId?: string | null
) =>
  interventionId && customFormId
    ? [orderDetailKey, 'simple', orderId, interventionId, customFormId]
    : [orderDetailKey, 'simple', orderId];
export const getPublicOrderQueryKeyBuilder = (id: string) => [orderDetailKey, id];
export const getExistingOrderKeyBuilder = (params: ExistingOrderQuery) => [
  'existingOrderQuery',
  params.customerId
];

export const getCustomFormMaterialQueryKeyBuilder = () => [customFormMaterialKey];

export const useGetOrdersQuery = (params: OrderTableQuery) => {
  return useQuery<TableResult<OrderAlt>, ApiError>(
    getTableQueryKey(ordersKey, params),
    () => ordersClient.getOrders(params),
    {
      keepPreviousData: true
    }
  );
};

export const useGetOrderDetailQuery = (id: string) => {
  return useQuery<OrderDetail, ApiError>(
    getOrderDetailQueryKeyBuilder(id),
    () => ordersClient.getOrderDetail(id)
    // {
    //   staleTime: GLOBAL_QUERY_STALE_TIME
    // }
  );
};

export const useGetSimpleOrderDetailQuery = (
  orderId: string,
  customFormId: string,
  interventionId: string
) => {
  return useQuery<OrderDetail, ApiError>(
    getOrderDetailQueryKeyBuilderSimple(orderId, interventionId, customFormId),
    () =>
      ordersClient.getSimpleOrderDetail({
        orderId,
        customFormId,
        interventionId
      })
  );
};

export const useGetOrderDetailQueryV2 = (id: string) => {
  return useQuery<OrderDetailAlt, ApiError>(getOrderDetailQueryKeyBuilderV2(id), () =>
    ordersClient.getOrderDetailV2(id)
  );
};

export const useGetPublicOrderQuery = (tenantCode: string, orderId: string) => {
  return useQuery<PublicGetPublicOrderDataQueryResult, ApiError>(
    getPublicOrderQueryKeyBuilder(orderId),
    () => ordersClient.getPublicOrder(tenantCode, orderId)
  );
};

export const useEditOrderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<OrderDetailAlt, ApiError, { orderId: string; data: Partial<OrderToEdit> }>(
    ordersClient.editOrder,
    {
      onSuccess: async (data, { orderId }) => {
        await queryClient.invalidateQueries(getOrdersQueryKeyBuilder());
        await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderSimple(orderId));
        queryClient.setQueryData<OrderDetailAlt>(getOrderDetailQueryKeyBuilderV2(orderId), data);
      }
    }
  );
};

export const useAskCustomerForInfoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(ordersClient.askCustomerForInfo, {
    onSuccess: async (data, orderId) => {
      await queryClient.invalidateQueries(getOrdersQueryKeyBuilder());
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderV2(orderId));
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useCompleteOrderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(ordersClient.completeOrder, {
    onSuccess: async (data, orderId) => {
      await queryClient.invalidateQueries(getOrdersQueryKeyBuilder());
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderV2(orderId));
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useReopenOrderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(ordersClient.reopenOrder, {
    onSuccess: async (data, orderId) => {
      await queryClient.invalidateQueries(getOrdersQueryKeyBuilder());
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderV2(orderId));
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useCancelOrderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { orderId: string; data: CancelOrderRequest }>(
    ordersClient.cancelOrder,
    {
      onSuccess: async (data, { orderId }) => {
        await queryClient.invalidateQueries(getOrdersQueryKeyBuilder());
        await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderV2(orderId));
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

export const useDeleteOrderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(ordersClient.deleteOrder, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(getOrdersQueryKeyBuilder());
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useAddOrderAttachmentData = () => {
  const queryClient = useQueryClient();

  return (orderId: string, attachment: Attachment) =>
    queryClient.setQueryData<OrderDetailAlt | undefined>(
      getOrderDetailQueryKeyBuilderV2(orderId),
      (oldData) =>
        oldData && {
          ...oldData,
          attachments: [...oldData.attachments, attachment]
        }
    );
};

export const useDeleteOrderAttachmentData = () => {
  const queryClient = useQueryClient();

  return (orderId: string, attachmentId: string) =>
    queryClient.setQueryData<OrderDetailAlt | undefined>(
      getOrderDetailQueryKeyBuilderV2(orderId),
      (oldData) =>
        oldData && {
          ...oldData,
          attachments: oldData.attachments.filter((attachment) => attachment.id !== attachmentId)
        }
    );
};

export const useAddCustomFormMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<OrderDetail, ApiError, { orderId: string; data: OrderCustomFormToAdd }>(
    ordersClient.addCustomForm,
    {
      onSuccess: async (data, { orderId }) => {
        queryClient.setQueryData<OrderDetail>(getOrderDetailQueryKeyBuilderV2(orderId), data);
      }
    }
  );
};

export const useEditCustomFormMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    OrderDetail,
    ApiError,
    {
      orderId: string;
      customFormId: string;
      data: OrderCustomFormToEdit;
    }
  >(ordersClient.editCustomForm, {
    onSuccess: async (data, { orderId }) => {
      queryClient.setQueryData<OrderDetail>(getOrderDetailQueryKeyBuilderV2(orderId), data);
    }
  });
};

export const useDeleteCustomFormMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      orderId: string;
      customFormId: string;
    }
  >(ordersClient.deleteCustomForm, {
    onSuccess: async (data, { orderId, customFormId }) => {
      queryClient.setQueryData<OrderDetail | undefined>(
        getOrderDetailQueryKeyBuilderV2(orderId),
        (oldData) =>
          oldData && {
            ...oldData,
            customForms: oldData.customForms.filter((x) => x.id !== customFormId)
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useSaveCustomFormProgressMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    OrderDetail,
    ApiError,
    {
      orderId: string;
      customFormId: string;
      data: string;
    }
  >(ordersClient.saveCustomFormProgress, {
    onSuccess: async (data, { orderId }) => {
      queryClient.setQueryData<OrderDetail>(getOrderDetailQueryKeyBuilderSimple(orderId), data);
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useSignOrderReportMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    OrderDetail,
    ApiError,
    {
      orderId: string;
      customFormId: string;
      reportId: string;
      data: FormData;
    }
  >(ordersClient.signInReport, {
    onSuccess: async (data, { orderId }) => {
      queryClient.setQueryData<OrderDetail>(getOrderDetailQueryKeyBuilderSimple(orderId), data);
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useAssignServiceObjectToOrderAttachmentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { serviceObjectId: string; attachmentId: string; orderId: string }
  >(commonClient.assignServiceObjectToAttachment, {
    onSuccess: (data, { serviceObjectId, attachmentId, orderId }) => {
      queryClient.setQueryData<OrderDetailAlt | undefined>(
        getOrderDetailQueryKeyBuilderV2(orderId),
        (oldData) =>
          oldData && {
            ...oldData,
            attachments: oldData.attachments.map((attachment) => {
              if (attachment.id === attachmentId) {
                return {
                  ...attachment,
                  serviceObjectId
                };
              }
              return attachment;
            })
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useEditOrderAttachmentData = () => {
  const queryClient = useQueryClient();

  return (orderId: string, updatedAttachment: Attachment) =>
    queryClient.setQueryData<OrderDetailAlt | undefined>(
      getOrderDetailQueryKeyBuilderV2(orderId),
      (oldData) =>
        oldData && {
          ...oldData,
          attachments: oldData.attachments.map((attachment) => {
            if (attachment.id === updatedAttachment.id) {
              return updatedAttachment;
            }
            return attachment;
          })
        }
    );
};

export const useAssignInterventionToOrderAttachmentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { orderId: string; attachmentId: string; interventionId: string }
  >(commonClient.assignInterventionToAttachment, {
    onSuccess: (data, { interventionId, attachmentId, orderId }) => {
      queryClient.setQueryData<OrderDetailAlt | undefined>(
        getOrderDetailQueryKeyBuilderV2(orderId),
        (oldData) =>
          oldData && {
            ...oldData,
            attachments: oldData.attachments.map((attachment) => {
              if (attachment.id === attachmentId) {
                return {
                  ...attachment,
                  interventionId
                };
              }
              return attachment;
            })
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useGetExistingOrdersQuery = (params: ExistingOrderQuery) => {
  return useQuery<TableResult<ExistingOrder>, ApiError>(
    getExistingOrderKeyBuilder(params),
    () => ordersClient.searchForExisting(params),
    {
      enabled: !!params.customerId
    }
  );
};

export const useGetInfinteOrderCustomFormMaterialQuery = (
  orderId: string,
  interventionId: string,
  customFormId: string,
  tableParams: CustomFormMaterialTableQuery
) => {
  return useInfiniteQuery<TableResultMaterialForCustomForm, ApiError>({
    queryKey: getTableQueryKey(customFormMaterialKey, tableParams, customFormId, true),
    queryFn: ({ pageParam = 1 }) =>
      ordersClient.getCustomFormMaterial({
        orderId,
        interventionId,
        customFormId,
        tableParams: {
          ...tableParams,
          page: pageParam
        }
      }),
    keepPreviousData: true,
    getNextPageParam: (lastPage, pages) => {
      const nextPageParam = getInfiniteNextPageParam(
        lastPage.data,
        pages.map((page) => page.data),
        defaultMaterialTableQuery.pageSize
      );

      return nextPageParam;
    }
  });
};
